import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Handle, Position } from '@xyflow/react';
import TemplatePreview from './TemplatePreview';
import { Edit, Reply, MessageSquare, Plus, Loader2 } from 'lucide-react';
import UseReplyModal from './UseReplyModal';
import axiosInstance from '../../../services/api/axios';
import { CloseButton, OutlineButton, PrimaryButton } from '../../../components/Buttons';

const ReplyNode = React.memo(({ data, id, selected, isConnectable }) => {
  const { label, headerType, headerText, body, buttons = [], onCreateTemplate, onAddReplyNode, isViewOnly, project } = data;

  console.log("view", isViewOnly);

  const isTemplateCreated = useMemo(() => Boolean(data?.templateName), [data?.templateName]);
  const [imageUrl, setImageUrl] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedReplyButtonIndex, setSelectedReplyButtonIndex] = useState(null);
  const [previousTemplates, setPreviousTemplates] = useState([]);
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false);
  const [isEditingLoading, setIsEditingLoading] = useState(false); // Loading state for Edit Template button
  const publishedRepliesCache = useRef({});

  // Memoize the image URL to prevent unnecessary re-renders
  const memoizedImageUrl = useMemo(() => {
    // Handle both headerMedia (local storage) and media (backend) structures
    const mediaData = data.headerMedia || data.media;
    
    if (data.headerType === 'image' && mediaData) {
      if (mediaData.content) {
        return `data:${mediaData.content_type};base64,${mediaData.content}`;
      }
      if (mediaData.base64) {
        return mediaData.base64;
      }
    }
    return null;
  }, [data.headerType, data.headerMedia, data.media]);

  useEffect(() => {
    let url;

    const setupImageUrl = async () => {
      try {
        // Use memoized base64 URL if available
        if (memoizedImageUrl) {
          setImageUrl(memoizedImageUrl);
          return;
        }

        // Handle file-based media - check both headerMedia and media structures
        const mediaData = data.headerMedia || data.media;
        if (data.headerType !== 'text' && mediaData?.file) {
          // Only create new object URL if we don't have one
          if (!objectUrl) {
            url = URL.createObjectURL(mediaData.file);
            setObjectUrl(url);
            setImageUrl(url);
          }
        } else {
          setImageUrl(null);
        }
      } catch (error) {
        console.error('Error setting up image URL:', error);
      }
    };

    setupImageUrl();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        setObjectUrl(null);
      }
    };
  }, [data.headerMedia?.file, data.media?.file, data.headerType, memoizedImageUrl, objectUrl]);

  useEffect(() => {

  }, [data, id]);

  const handleButtonClick = useCallback(() => {
    setIsEditingLoading(true);

 
    setTimeout(() => {
      const storedTemplate = localStorage.getItem(`template-${id}`);

      if (storedTemplate) {
        try {
          const parsedTemplate = JSON.parse(storedTemplate);
         
          onCreateTemplate?.(id);
        } catch (error) {
          console.error("Error parsing stored template:", error);
         
          onCreateTemplate?.(id);
        }
      } else {
      
        onCreateTemplate?.(id);
      }
      setIsEditingLoading(false);
    }, 500); 
  }, [id, onCreateTemplate]);

  const handleAddReply = useCallback((buttonIndex) => {
    if (typeof onAddReplyNode === 'function') {
      onAddReplyNode(id, buttonIndex);
    }
  }, [id, onAddReplyNode]);

  
  const templatePreview = useMemo(() => {
   
    const mediaData = data.headerMedia || data.media;
    
    return (
      <TemplatePreview
        key={`preview-${id}`}
        headerType={data.headerType}
        headerText={data.headerText}
        headerMedia={mediaData}
        variable={data.variable}
        body={data.body}
        buttons={data.buttons}
        imageUrl={imageUrl}
      />
    );
  }, [data.headerType, data.headerText, data.headerMedia, data.media, data.variable, data.body, data.buttons, id, imageUrl]);

  const nodeNumber = useMemo(() => {
    const match = id.match(/\d+$/);
    return match ? match[0] : '';
  }, [id]);

 
  const handleStyle = {
    background: '#F43F5E', 
    width: '12px',
    height: '12px',
    border: '2px solid white',
    borderRadius: '50%',
    cursor: 'crosshair',
    zIndex: 1000,
    boxShadow: '0 0 4px rgba(0, 0, 0, 0.3)'
  };


  const buttonHandleStyle = {

    width: '10px',
    height: '10px',
    border: '1.5px solid white',
    borderRadius: '50%',
    cursor: 'crosshair',
    zIndex: 1000,
    boxShadow: '0 0 4px rgba(0, 0, 0, 0.3)'
  };

  // Filter quick reply buttons
  const quickReplyButtons = buttons.filter(button => {

    return button.typeOfAction === 'quick_reply';
  });

  
  const getCurrentLeadType = useCallback(() => {
    return localStorage.getItem('current_leadType') || 'standard';
  }, []);

  const shouldShowButtons = useCallback(() => {
    const leadType = getCurrentLeadType();
    return !['birthday_template', 'festival_template'].includes(leadType);
  }, [getCurrentLeadType]);

  const loadPreviousTemplates = async () => {
    if (!project?.customerId || !project?.projectId) {
      setPreviousTemplates([]);
      return;
    }

    const cacheKey = `replies_${project.customerId}_${project.projectId}`;
 
    if (publishedRepliesCache.current[cacheKey]) {
     
      setPreviousTemplates(publishedRepliesCache.current[cacheKey]);
      return;
    }

   
    const sessionCache = sessionStorage.getItem(cacheKey);
    if (sessionCache) {
      try {
        const cachedData = JSON.parse(sessionCache);
        publishedRepliesCache.current[cacheKey] = cachedData;
        setPreviousTemplates(cachedData);
      
        return;
      } catch (error) {
        console.error("Error parsing session cache:", error);
        sessionStorage.removeItem(cacheKey);
      }
    }

    setIsLoadingWorkflows(true);
    try {
      const response = await axiosInstance.get('/customer/whatsapp/drip-campaign', {
        params: {
          customerId: project.customerId,
          projectId: project.projectId
        }
      });

      const replyTemplates = (response.templates || [])
        .filter(template => template.templateType === 'REPLY_NODE_TEMPLATE');


      // Cache the results
      publishedRepliesCache.current[cacheKey] = replyTemplates;
    
      setPreviousTemplates(replyTemplates);
     
    } catch (err) {
      console.error('Error fetching replies:', err);
      setPreviousTemplates([]);
    } finally {
      setIsLoadingWorkflows(false);
    }
  };

  const handleReplyOptionClick = async (index) => {
  
    setSelectedReplyButtonIndex(index);
    setShowReplyModal(true);
   

    if (!project?.customerId || !project?.projectId) {
      console.error('Missing project data:', project);
      return;
    }

    await loadPreviousTemplates();
  };

  const handleCreateNewReply = () => {
    setShowReplyModal(false);
    if (typeof data.onAddReplyNode === 'function') {
      data.onAddReplyNode(id, selectedReplyButtonIndex);
    }
  };

  const handleUseExistingTemplate = (template) => {
    setShowReplyModal(false);
    console.log('template from reply modal reply node==================', template);

    if (typeof data.onAddReplyNode === 'function') {
      // Add the reply node
      data.onAddReplyNode(id, selectedReplyButtonIndex);
      const apiButtons = template.components?.find(c => c.type === 'BUTTONS')?.buttons || [];
      const formButtons = apiButtons.map(button => {
        const buttonScaffold = {
            buttonText: button.text || '',
            typeOfAction: button.type.toLowerCase() ,
            type: button.type.toLowerCase(),
            websiteURL: button.url || '',
            phoneNumber: button.phone_number || '',
            offerCode: button.offer_code || '',
        };
        switch (button.type) {
          case 'URL':
             
              if (button.url && button.url.toLowerCase().includes('/site-visit?')) {
                  return { ...buttonScaffold, typeOfAction: 'site_visit', websiteURL: button.url || '' };
              }
              return { ...buttonScaffold, typeOfAction: 'visit_website', websiteURL: button.url || '' };
          
          case 'PHONE_NUMBER':
              return { ...buttonScaffold, typeOfAction: 'call_phone_number', phoneNumber: button.phone_number || '' };

          case 'COPY_CODE':
              return { ...buttonScaffold, typeOfAction: 'copy_offer_code', offerCode: button.offer_code || '' };
          
          case 'QUICK_REPLY':
          default:
            
              return { ...buttonScaffold, typeOfAction: 'custom', label: button.text || '' };
      }
  });
      

      // Extract template data
      const templateData = {
        templateName: (template.friendlyName) + ' (Copy)',
        headerType: template.components?.find(c => c.type === 'HEADER')?.format?.toLowerCase() || 'text',
        headerText: template.components?.find(c => c.type === 'HEADER')?.text || '',
        headerMedia: template.components?.find(c => c.type === 'HEADER')?.example?.header_handle || null,
        body: template.components?.find(c => c.type === 'BODY')?.text || '',
        buttons: formButtons,
        leadType: template.persona || [],
        conditions: template.conditions || '',
        conditionString: template.conditions || '',
        whatsappTemplateStatus: 'NOT_PUBLISHED',
        templateType: 'REPLY_NODE_TEMPLATE',
        isPublished: false,
        isNotificationSent: false,
        order: 0,
        customerWhatsappTemplateId: undefined
      };

      // Save the template data to localStorage
      const currentLeadType = localStorage.getItem('current_leadType') || 'standard';

      // Use setTimeout to ensure the node is created first
      setTimeout(() => {
        const replyNodes = document.querySelectorAll('[data-id^="reply-"]');
        if (replyNodes.length > 0) {
          const lastReplyNode = replyNodes[replyNodes.length - 1];
          const newNodeId = lastReplyNode.getAttribute('data-id');

          // Save the template data with the correct node ID
          localStorage.setItem(`template-${currentLeadType}-${newNodeId}`, JSON.stringify(templateData));

          // Update the node data
          if (data.onCreateTemplate) {
            data.onCreateTemplate(newNodeId, templateData);
          }
        }
      }, 100);
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-md border-2 border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200`}
      style={{ minWidth: '280px', maxWidth: '320px' }}
    >
      {/* Standard handles */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{
          ...handleStyle,
          background: '#F43F5E', // flamingo-600
          border: '2px solid white',
          boxShadow: '0 0 4px rgba(244, 63, 94, 0.3)' // flamingo shadow
        }}
        isConnectable={!isViewOnly && isConnectable}
      />

      <Handle
        type="target"
        position={Position.Left}
        isConnectable={isConnectable}
        style={{
          ...handleStyle,
          background: '#F43F5E', // flamingo-600
          border: '2px solid white',
          boxShadow: '0 0 4px rgba(244, 63, 94, 0.3)' // flamingo shadow
        }}
      />

      {/* Node Header */}
      <div className="text-gray-600 py-2 px-3 font-medium flex items-center justify-between shadow-sm">
        <div className="flex items-center">
          <MessageSquare size={18} className="mr-2 flex-shrink-0" />
          <span className="text-sm truncate max-w-[230px]">
            {data?.friendlyName || (data.sourceButtonText ?
              `Reply to: ${data.sourceButtonText}` :
              `Reply #${nodeNumber}`)}
          </span>
        </div>
      </div>

      {/* Template Preview */}
      <div className="px-3 pt-3">
        <div className="border border-flamingo-100 rounded-md overflow-hidden bg-flamingo-50/50">
          {templatePreview}
        </div>
      </div>

      {/* Action Buttons & Reply Options */}
      <div className="p-3  border-t border-flamingo-200 mt-3">
        {/* Show edit button for both unpublished and published templates */}
        {(!isViewOnly || data.isPublished) && (
          <OutlineButton
            onClick={handleButtonClick}
            className='w-full'
            disabled={isEditingLoading}
          >
            {isEditingLoading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Edit size={16} className="mr-2" />
                {data.isPublished ? 'Edit Published Template' : 'Edit Template'}
              </>
            )}
          </OutlineButton>
        )}

        {isTemplateCreated && data.buttons && data.buttons.filter(button => button.type === 'quick_reply' || button.typeOfAction === 'quick_reply').length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
          {isViewOnly ? (
                      <p className="text-xs font-medium text-gray-400 mb-1">
                        BUTTONS
                      </p>
                    ) : (
                      <p className="text-xs font-medium text-gray-600 mb-1">REPLY OPTIONS:</p>
                    )}
            <div className="space-y-2">
              {data.buttons
                .filter(button => button.type === 'quick_reply' || button.typeOfAction === 'quick_reply')
                .map((button, index) => (
                  <div key={index} className="relative flex items-center">
                    <button
                      onClick={!isViewOnly ? () => handleReplyOptionClick(index) : undefined}
                      className={`w-full py-1.5 px-3 text-sm border border-gray-300 rounded-md bg-white ${isViewOnly || data.isPublished ? 'cursor-default' : 'hover:bg-gray-50 hover:border-gray-400 cursor-pointer'
                        } text-gray-700 font-medium flex items-center justify-between transition-colors shadow-sm`}
                    >
                      <span className="truncate max-w-[200px]">{button.buttonText || `Button ${index + 1}`}</span>
                      <Reply size={14} className="flex-shrink-0" />
                    </button>
                    <Handle
                      type="source"
                      position={Position.Right}
                      id={`button-${index}`}
                      style={{
                        ...buttonHandleStyle,
                        left: '100%',
                        marginLeft: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        position: 'absolute'
                      }}
                      isConnectable={!isViewOnly && isConnectable}
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Replace the Modal with UseReplyModal */}
      <UseReplyModal
        open={showReplyModal}
        onClose={() => setShowReplyModal(false)}
        onCreateNewReply={handleCreateNewReply}
        onUseExistingTemplate={handleUseExistingTemplate}
        previousTemplates={previousTemplates}
        isLoadingWorkflows={isLoadingWorkflows}
      />
    </div>
  );
});

ReplyNode.displayName = 'ReplyNode';

export default ReplyNode;